import React, { useState, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Users, Calendar, Package, MessageSquare, Menu, X } from 'lucide-react';
import { toast } from 'react-toastify';

// ✅ Lazy load all heavy components
const AdminProducts = lazy(() => import('./AdminProducts'));
const TestimonialsComponent = lazy(() => import('./Testimonials'));
const BlogsComponent = lazy(() => import('./Blogs'));
const EventsComponent = lazy(() => import('./Events'));
const SubscribersComponent = lazy(() => import('./Subscribers'));
const ApplicationsComponent = lazy(() => import('./Applications'));
const JobPostings = lazy(() => import('./JobPostings'));
const EventConfig = lazy(() => import('./EventConfig'));

// Main Dashboard Component
const Dashboard = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    // Get current active menu from URL path
    const getCurrentMenu = () => {
        const path = location.pathname.split('/').pop();
        return path || 'products';
    };

    const activeMenu = getCurrentMenu();

    const token = localStorage.getItem("token");
    let isAuthenticated = false;

    if (token) {
        isAuthenticated = true;
    }

    const handleLogOut = () => {
        localStorage.removeItem('token');
        toast.success('Logged Out Successfully!');
        navigate('/admin/login');
    };

    const menuItems = [
        { id: 'products', label: 'Products', icon: Package, path: '/admin/products' },
        { id: 'events', label: 'Events', icon: Calendar, path: '/admin/events' },
        { id: 'blogs', label: 'Blogs', icon: MessageSquare, path: '/admin/blogs' },
        { id: 'testimonials', label: 'Testimonials', icon: Users, path: '/admin/testimonials' },
        { id: 'contactus', label: 'Contact Us', icon: Users, path: '/admin/contactus' },
        { id: 'subscribers', label: 'Subscribers', icon: Users, path: '/admin/subscribers' },
        { id: 'jobpostings', label: 'Job Postings', icon: Users, path: '/admin/jobpostings' },
        { id: 'eventlinks', label: 'Event Links', icon: Users, path: '/admin/eventlinks' },
    ];

    const handleMenuClick = (path) => {
        navigate(path);
    };

    return (
        <div className="min-h-screen bg-gray-100 flex overflow-auto">
            {/* Sidebar */}
            <div className={`${isSidebarOpen ? 'w-64' : 'w-16'} bg-white shadow-lg transition-all duration-300 ease-in-out fixed h-full z-10`}>
                <div className="p-4">
                    <div className="flex items-center justify-between">
                        <h1 className={`font-bold text-xl text-gray-800 ${!isSidebarOpen && 'hidden'}`}>Dashboard</h1>
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>

                <nav className="mt-8">
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.id}
                                onClick={() => handleMenuClick(item.path)}
                                className={`w-full flex items-center px-4 py-3 text-left transition-colors duration-200 ${activeMenu === item.id
                                    ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                                    : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                            >
                                <Icon size={20} className="mr-3" />
                                <span className={`${!isSidebarOpen && 'hidden'}`}>{item.label}</span>
                            </button>
                        );
                    })}
                    <button
                        onClick={handleLogOut}
                        className={`w-full flex items-center px-2 py-2 text-left duration-200 btn btn-primary mt-3`}
                    >
                        <span className={`${!isSidebarOpen && 'hidden'}`}>Log Out</span>
                    </button>
                </nav>
            </div>

            {/* Main Content */}
            <div className={`flex-1 p-8 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'ml-64' : 'ml-16'}`}>
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 capitalize">{activeMenu}</h2>
                        <p className="text-gray-600 mt-1">Manage your {activeMenu} here</p>
                    </div>
                </div>

                {/* Lazy-loaded routes with fallback */}
                <Suspense fallback={<div className="text-center mt-10">Loading...</div>}>
                    <Routes>
                        <Route path="products" element={<AdminProducts />} />
                        <Route path="events" element={<EventsComponent />} />
                        <Route path="blogs" element={<BlogsComponent />} />
                        <Route path="testimonials" element={<TestimonialsComponent />} />
                        <Route path="contactus" element={<ApplicationsComponent />} />
                        <Route path="subscribers" element={<SubscribersComponent />} />
                        <Route path="jobpostings" element={<JobPostings />} />
                        <Route path="eventlinks" element={<EventConfig />} />
                        <Route path="" element={<AdminProducts />} />
                    </Routes>
                </Suspense>
            </div>
        </div>
    );
};

export default Dashboard;
